/**
 * @fileoverview Menu Access Context
 * 
 * @description
 * Provides menu access information throughout the application.
 * Loads user's allowed menus from API and exposes helper functions
 * to check access to specific routes/menus.
 * 
 * This context is used by:
 * - NavigationBar: to show/hide menu items
 * - UserManual: to filter help sections based on accessible modules
 * 
 * @version 1.0.0
 * @date 2026-01-09
 */

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from './AuthContext';
import { useAccessManifest } from './AccessManifestContext';
import { isOfflineSessionToken } from '../utils/offlineAuth';
import {
  expandMenuRoutesWithAliases,
  hasMenuRouteWithAliases,
} from '../utils/menuRouteAliases';

const MenuAccessContext = createContext(null);

export const MenuAccessProvider = ({ children }) => {
  const { user } = useAuth();
  const { loaded: manifestLoaded, manifest } = useAccessManifest();
  const [allowedMenus, setAllowedMenus] = useState([]);
  const [menusLoaded, setMenusLoaded] = useState(false);

  /**
   * Load user's allowed menus from the API
   */
  const loadUserMenus = useCallback(async () => {
    const currentToken = (() => {
      try {
        return localStorage.getItem('token');
      } catch {
        return null;
      }
    })();
    const offlineSession = isOfflineSessionToken(currentToken);

    if (offlineSession) {
      // Offline-safe fallback: always expose the critical Drill Progress Map route.
      // Include the legacy path so offline sessions bookmarked on /operations/field-operations
      // still resolve until the back-compat redirect window in QhseOperationsRoutes.js closes.
      setAllowedMenus(['/drilling/progress-map', '/operations/field-operations']);
      setMenusLoaded(true);
      return;
    }

    if (!user?.id) {
      setAllowedMenus([]);
      setMenusLoaded(true);
      return;
    }

    // Superadmin has access to all menus
    if (user.role === 'superadmin') {
      setAllowedMenus(['all']);
      setMenusLoaded(true);
      return;
    }

    if (manifestLoaded && Array.isArray(manifest?.allowedRoutes) && manifest.allowedRoutes.length > 0) {
      setAllowedMenus(expandMenuRoutesWithAliases(manifest.allowedRoutes));
      setMenusLoaded(true);
      return;
    }

    try {
      const response = await api.get(`/users/${user.id}/menus`);
      const menuData = response.data;
      const menuRoutes = menuData.map((menu) => menu.route);
      setAllowedMenus(expandMenuRoutesWithAliases(menuRoutes));
    } catch (err) {
      console.error('Error loading user menus:', err);
      // If error, default to no menus (restrictive)
      setAllowedMenus([]);
    } finally {
      setMenusLoaded(true);
    }
  }, [user?.id, user?.role, manifestLoaded, manifest?.allowedRoutes]);

  // Load menus when user changes
  useEffect(() => {
    setMenusLoaded(false);
    loadUserMenus();
  }, [loadUserMenus]);

  /**
   * Check if user has access to a specific menu route
   * @param {string} menuRoute - The menu route to check (e.g., '/inventory')
   * @returns {boolean} - True if user has access
   */
  const hasMenuAccess = useCallback((menuRoute) => {
    if (hasMenuRouteWithAliases(allowedMenus, menuRoute)) return true;
    // Petty Cash: "Petty Cash Funds" menu grants access to fund detail URLs (/funds/:id, /funds/:id/edit)
    if (
      typeof menuRoute === 'string' &&
      menuRoute.startsWith('/finance/petty-cash/funds/') &&
      allowedMenus.includes('/finance/petty-cash/funds')
    ) {
      return true;
    }
    return false;
  }, [allowedMenus]);

  /**
   * Check if user has access to a parent menu or any of its child routes.
   * Use this for top-level menu visibility: show the parent menu
   * if the user has access to the parent itself or any submenu under it.
   * @param {string} parentRoute - The parent route (e.g., '/hr')
   * @param {string[]} childRoutes - Array of child routes
   * @returns {boolean}
   */
  const hasMenuAccessOrChild = useCallback((parentRoute, childRoutes = []) => {
    if (hasMenuRouteWithAliases(allowedMenus, parentRoute)) return true;
    return childRoutes.some((r) => hasMenuRouteWithAliases(allowedMenus, r));
  }, [allowedMenus]);

  /**
   * Check if user is superadmin (has all access)
   * @returns {boolean}
   */
  const isSuperAdmin = useCallback(() => {
    return allowedMenus.includes('all');
  }, [allowedMenus]);

  /**
   * Get list of allowed menu routes
   * @returns {string[]} - Array of menu routes or ['all'] for superadmin
   */
  const getAllowedMenuRoutes = useCallback(() => {
    return [...allowedMenus];
  }, [allowedMenus]);

  const value = {
    allowedMenus,
    menusLoaded,
    hasMenuAccess,
    hasMenuAccessOrChild,
    isSuperAdmin,
    getAllowedMenuRoutes,
    refreshMenus: loadUserMenus
  };

  return (
    <MenuAccessContext.Provider value={value}>
      {children}
    </MenuAccessContext.Provider>
  );
};

/**
 * Hook to access menu permissions
 * @returns {Object} Menu access context value
 */
export const useMenuAccess = () => {
  const context = useContext(MenuAccessContext);
  if (!context) {
    throw new Error('useMenuAccess must be used within a MenuAccessProvider');
  }
  return context;
};

export default MenuAccessContext;
